<template>
    <div class="mt-6 content">
        <div class="columns is-gapless is-mobile">
            <div class="column is-narrow"><i class="fad fa-fw fa-flag-swallowtail has-text-white mr-3 is-size-4"></i></div>
            <div class="column">
                <h3 class="has-text-white">Milestones & Reflections</h3>
                <p class="has-text-white">Milestones act as checkpoints during your coursework. As you progress through your course you may be stopped to complete one of these checkpoints.</p>

                <div class="columns is-gapless mb-5 mt-5">
                    <div class="column is-narrow has-text-white"><i class="far fa-fw fa-info-circle mr-2"></i></div>
                    <div class="column has-text-white">
                        <div>Please note that these Milestones & Reflections, along with your Study Log, are further documentation of your coursework. We need this documentation when we review your course and it's required should your course be selected for audit. It is important that your reflections are authentic and thoughtful. Repetitive (or copy/pasted) reflections will be rejected and you will be asked to rewrite them or provide additional artifacts to document your course.</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading -->
        <div class="mt-6 pb-6" v-if="loading">
            <ui-card class="pt-0 pl-4 pr-6">
                <div class="py-2 has-text-centered">
                    <i class="fas fa-spin fa-spinner fa-fw mr-2"></i>Loading milestones...
                </div>
            </ui-card>
        </div>

        <!-- Not a self-directed course -->
        <div class="mt-6 pb-6" v-else-if="!isSelfDirected">
            <ui-card class="pt-0 pl-4 pr-6">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-info-circle mr-2"></i></div>
                    <div class="column">
                        <div class="has-text-weight-bold">This course does not have any required milestones or reflections.</div>
                    </div>
                </div>
            </ui-card>
        </div>

        <!-- Milestones loaded -->
        <div class="mt-6 pb-6" v-else>
            <!-- Current Milestone - Working toward it (show instructions, no form yet) -->
            <ui-card class="mb-6 pr-6" v-if="current && current.status === 'pending' && !isLocked">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-flag-swallowtail mr-2 is-size-5 has-text-info"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">Milestone {{ current.milestone_number }}: {{ getMilestoneRange(current) }}</h4>
                        <p class="has-text-grey-dark mb-4">You've logged <strong>{{ totalHours.toFixed(1) }} hours</strong>. Your next checkpoint is at <strong>{{ current.hour_threshold }} hours</strong>.</p>

                        <progress class="progress is-info mt-4 mb-5" :value="totalHours" :max="current.hour_threshold"></progress>

                        <!-- Suggested path from admin -->
                        <div v-if="current.instructions">
                            <label class="label has-text-grey">Suggestions for this milestone:</label>
                            <div class="notification is-light instructions-box">
                                <div v-html="current.instructions"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </ui-card>

            <!-- Current Milestone - Reached threshold, needs reflection -->
            <ui-card class="mb-6 pr-6" v-else-if="current && current.status === 'pending' && isLocked">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-location-check mr-2 is-size-5 has-text-warning"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">
                            Milestone {{ current.milestone_number }}: {{ getMilestoneRange(current) }}
                            <span v-if="isStem" class="tag is-success is-light ml-2"><i class="fas fa-flask mr-1"></i>STEM</span>
                        </h4>
                        <p class="has-text-grey-dark mb-4">
                            You've reached {{ totalHours.toFixed(1) }} hours. Please complete the following and submit your
                            reflection<span v-if="isStem && stemWorkLogged"> for your STEM activities</span> to continue.
                        </p>

                        <!-- Suggested path from admin -->
                        <div v-if="current.instructions" class="mb-5">
                            <label class="label has-text-grey">Suggestions for this milestone:</label>
                            <div class="notification is-light instructions-box">
                                <div v-html="current.instructions"></div>
                            </div>
                        </div>

                        <!-- STEM clock hour requirements — only when STEM work was logged this window -->
                        <b-message v-if="isStem && stemWorkLogged" type="is-warning" class="mb-5">
                            <strong><i class="fas fa-flask mr-2"></i>STEM Clock Hour Requirements</strong>
                            <p class="mt-2 is-size-6">
                                You logged time against STEM objectives this milestone. For those hours to be accepted as STEM
                                time, your reflection <strong>must address all three</strong> of the following:
                            </p>
                            <ol class="is-size-6 ml-5 mt-2">
                                <li>How does this STEM activity have an impact on the STEM student experience? Describe.</li>
                                <li>What examples AND resources were used with students or with other educators? Describe.</li>
                                <li>How does the STEM activity provide examples or resources about STEM-related career choices to use with students? Describe.</li>
                            </ol>
                        </b-message>
                        <p v-else-if="isStem" class="is-size-7 has-text-grey mb-5">
                            <i class="fas fa-flask mr-1"></i>No STEM time logged this milestone &mdash; a standard reflection is
                            all that is required. Log time against your STEM objective to earn STEM clock hours.
                        </p>

                        <!-- Study log for this milestone window, for reference while writing -->
                        <div v-if="current.study_log && current.study_log.session_count" class="mb-5">
                            <label class="label has-text-grey">Your study log for this milestone, for reference:</label>
                            <div class="notification is-light is-size-7 study-log-box">
                                <div v-for="(e, i) in current.study_log.entries" :key="i" class="mb-1">
                                    <span class="has-text-weight-bold mr-2">{{ e.date | moment('MMM D') }}</span>
                                    {{ e.activity || 'Course session' }}
                                    <span class="tag is-white log-time-tag ml-1">{{ formatLogMins(e.mins) }}</span>
                                    <span v-if="e.stem" class="tag is-success is-light ml-1"><i class="fas fa-flask mr-1"></i>STEM</span>
                                    <span v-if="e.starts_before_window || e.extends_beyond_window" class="has-text-grey ml-1">(spans milestone boundary)</span>
                                </div>
                                <p v-if="current.study_log.truncated_count" class="has-text-grey mt-1">&hellip;and {{ current.study_log.truncated_count }} more sessions</p>
                            </div>
                        </div>

                        <div class="mt-5">
                            <b-field label="Your Reflection">
                                <b-input
                                    type="textarea"
                                    v-model="reflectionText"
                                    placeholder="Please write a thoughtful reflection about your progress, what you've learned, and how you're applying it..."
                                    rows="6"
                                ></b-input>
                            </b-field>
                            <div class="is-size-7 has-text-right draft-status">
                                <span v-if="draftSaving"><i class="fas fa-spinner fa-spin mr-2"></i>Saving draft...</span>
                                <span v-else-if="draftSaveError" class="has-text-danger"><i class="fas fa-exclamation-triangle mr-2"></i>Could not save draft. Retrying on next change.</span>
                                <span v-else-if="draftSavedAt" class="has-text-grey"><i class="fas fa-cloud-check mr-2"></i>Draft saved {{ formatTime(draftSavedAt) }}</span>
                            </div>
                        </div>

                        <div class="mt-4">
                            <label class="label is-size-7 has-text-grey">Attachments (optional) &mdash; add any documents or artifacts from this milestone</label>
                            <div v-if="current.files && current.files.length" class="mb-3">
                                <div v-for="f in current.files" :key="f.id" class="mb-1">
                                    <a :href="f.preview_url || f.download_url" target="_blank" rel="noopener"><i class="far fa-paperclip mr-1"></i>{{ f.title }}</a>
                                    <span class="is-size-7 has-text-grey ml-2">{{ formatFileSize(f.size) }}</span>
                                    <a href="javascript:void(0)" class="ml-2" @click="deleteAttachment(f)" title="Remove"><i class="far fa-circle-xmark has-text-danger"></i></a>
                                </div>
                            </div>
                            <b-button size="is-small" @click="showAttachPanel = true">
                                <i class="far fa-paperclip mr-2"></i>Attach a file
                            </b-button>
                        </div>

                        <b-button
                            type="is-success"
                            class="mt-5"
                            @click="submitReflection"
                            :loading="submitting"
                            :disabled="!reflectionText.trim()"
                        >
                            <strong>Submit Reflection</strong>
                            <i class="far fa-arrow-right-long ml-3"></i>
                        </b-button>
                    </div>
                </div>
            </ui-card>

            <!-- Current Milestone - Submitted (pending review) -->
            <ui-card class="mb-6 pr-6" v-else-if="current && current.status === 'submitted'">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-clock mr-2 is-size-5 has-text-info"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">Milestone {{ current.milestone_number }}: {{ getMilestoneRange(current) }}</h4>
                        <div class="notification is-info is-light mt-4">
                            <i class="fas fa-clock mr-2"></i>
                            <strong>Pending Review</strong>
                            <p class="mt-2">
                                Your reflection has been submitted and is awaiting review. You'll receive an email as soon as it
                                has been reviewed. If it's approved, you can continue logging study time right away. If changes
                                are requested, the reviewer's feedback will be emailed to you and shown here &mdash; update your
                                reflection to address the feedback and resubmit to continue your course.
                            </p>
                        </div>
                        <div class="mt-4">
                            <label class="label has-text-grey">Your submitted reflection:</label>
                            <p class="content-text">{{ current.reflection_text }}</p>
                        </div>

                        <div v-if="current.files && current.files.length" class="mt-4">
                            <label class="label is-size-7 has-text-grey">Attached files:</label>
                            <div v-for="f in current.files" :key="f.id" class="mb-1">
                                <a :href="f.preview_url || f.download_url" target="_blank" rel="noopener"><i class="far fa-paperclip mr-1"></i>{{ f.title }}</a>
                                <span class="is-size-7 has-text-grey ml-2">{{ formatFileSize(f.size) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </ui-card>

            <!-- Current Milestone - Changes Requested -->
            <ui-card class="mb-6 pr-6" v-else-if="current && current.status === 'changes_requested'">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-exclamation-triangle mr-2 is-size-5 has-text-warning"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">Milestone {{ current.milestone_number }}: {{ getMilestoneRange(current) }}</h4>

                        <b-message type="is-warning" class="mt-4">
                            <strong><i class="fas fa-comment-dots mr-2"></i>Changes Requested</strong>
                            <p class="mt-2">{{ current.admin_feedback }}</p>
                        </b-message>

                        <!-- Instructions from admin -->
                        <div v-if="current.instructions" class="mb-5">
                            <label class="label has-text-grey">Original instructions:</label>
                            <div class="notification is-light instructions-box">
                                <div v-html="current.instructions"></div>
                            </div>
                        </div>

                        <div class="mt-4">
                            <label class="label has-text-grey">Your previous reflection:</label>
                            <p class="content-text mb-4">{{ current.reflection_text }}</p>
                        </div>

                        <!-- STEM clock hour requirements — only when STEM work was logged this window -->
                        <b-message v-if="isStem && stemWorkLogged" type="is-warning" class="mb-5">
                            <strong><i class="fas fa-flask mr-2"></i>STEM Clock Hour Requirements</strong>
                            <p class="mt-2 is-size-6">
                                You logged time against STEM objectives this milestone. For those hours to be accepted as STEM
                                time, your reflection <strong>must address all three</strong> of the following:
                            </p>
                            <ol class="is-size-6 ml-5 mt-2">
                                <li>How does this STEM activity have an impact on the STEM student experience? Describe.</li>
                                <li>What examples AND resources were used with students or with other educators? Describe.</li>
                                <li>How does the STEM activity provide examples or resources about STEM-related career choices to use with students? Describe.</li>
                            </ol>
                        </b-message>
                        <p v-else-if="isStem" class="is-size-7 has-text-grey mb-5">
                            <i class="fas fa-flask mr-1"></i>No STEM time logged this milestone &mdash; a standard reflection is
                            all that is required. Log time against your STEM objective to earn STEM clock hours.
                        </p>

                        <!-- Study log for this milestone window, for reference while writing -->
                        <div v-if="current.study_log && current.study_log.session_count" class="mb-5">
                            <label class="label has-text-grey">Your study log for this milestone, for reference:</label>
                            <div class="notification is-light is-size-7 study-log-box">
                                <div v-for="(e, i) in current.study_log.entries" :key="i" class="mb-1">
                                    <span class="has-text-weight-bold mr-2">{{ e.date | moment('MMM D') }}</span>
                                    {{ e.activity || 'Course session' }}
                                    <span class="tag is-white log-time-tag ml-1">{{ formatLogMins(e.mins) }}</span>
                                    <span v-if="e.stem" class="tag is-success is-light ml-1"><i class="fas fa-flask mr-1"></i>STEM</span>
                                    <span v-if="e.starts_before_window || e.extends_beyond_window" class="has-text-grey ml-1">(spans milestone boundary)</span>
                                </div>
                                <p v-if="current.study_log.truncated_count" class="has-text-grey mt-1">&hellip;and {{ current.study_log.truncated_count }} more sessions</p>
                            </div>
                        </div>

                        <div class="mt-5">
                            <b-field label="Updated Reflection">
                                <b-input
                                    type="textarea"
                                    v-model="reflectionText"
                                    placeholder="Please update your reflection based on the feedback..."
                                    rows="6"
                                ></b-input>
                            </b-field>
                            <div class="is-size-7 has-text-right draft-status">
                                <span v-if="draftSaving"><i class="fas fa-spinner fa-spin mr-2"></i>Saving draft...</span>
                                <span v-else-if="draftSaveError" class="has-text-danger"><i class="fas fa-exclamation-triangle mr-2"></i>Could not save draft. Retrying on next change.</span>
                                <span v-else-if="draftSavedAt" class="has-text-grey"><i class="fas fa-cloud-check mr-2"></i>Draft saved {{ formatTime(draftSavedAt) }}</span>
                            </div>
                        </div>

                        <div class="mt-4">
                            <label class="label is-size-7 has-text-grey">Attachments (optional)</label>
                            <div v-if="current.files && current.files.length" class="mb-3">
                                <div v-for="f in current.files" :key="f.id" class="mb-1">
                                    <a :href="f.preview_url || f.download_url" target="_blank" rel="noopener"><i class="far fa-paperclip mr-1"></i>{{ f.title }}</a>
                                    <span class="is-size-7 has-text-grey ml-2">{{ formatFileSize(f.size) }}</span>
                                    <a href="javascript:void(0)" class="ml-2" @click="deleteAttachment(f)" title="Remove"><i class="far fa-circle-xmark has-text-danger"></i></a>
                                </div>
                            </div>
                            <b-button size="is-small" @click="showAttachPanel = true">
                                <i class="far fa-paperclip mr-2"></i>Attach a file
                            </b-button>
                        </div>

                        <b-button
                            type="is-success"
                            class="mt-5"
                            @click="submitReflection"
                            :loading="submitting"
                            :disabled="!reflectionText.trim()"
                        >
                            <strong>Resubmit Reflection</strong>
                            <i class="far fa-arrow-right-long ml-3"></i>
                        </b-button>
                    </div>
                </div>
            </ui-card>

            <!-- All milestones complete -->
            <ui-card class="mb-6 pr-6" v-else-if="!current && milestones.length > 0">
                <div class="columns is-gapless mb-5 pt-5">
                    <div class="column is-narrow"><i class="far fa-fw fa-trophy mr-2 is-size-5 has-text-success"></i></div>
                    <div class="column">
                        <h4 class="has-text-weight-bold">All Milestones Complete!</h4>
                        <p>You've completed all <strong>{{ milestones.length }}</strong> milestones for this course. Total time logged: <strong>{{ totalTimeFormatted }}</strong>.</p>
                    </div>
                </div>
            </ui-card>

            <!-- No milestones yet -->
            <ui-card class="pt-0" v-else-if="!current">
                <div class="columns is-gapless pt-2">
                    <div class="column is-narrow"><i class="far fa-fw fa-flag-swallowtail mr-2"></i></div>
                    <div class="column">
                        <div class="has-text-weight-bold">You don't have any milestones for this course yet.</div>
                        <div class="mt-1">Milestones appear once your <em>Course Study Plan</em> is approved and you begin logging hours.</div>
                    </div>
                </div>
            </ui-card>

            <!-- Completed Milestones History -->
            <template v-if="completedMilestones.length > 0">
                <h4 class="has-text-white mt-6 mb-4"><i class="far fa-history mr-2"></i>Completed Milestones</h4>

                <ui-card class="mb-4 pr-6" v-for="m in completedMilestones" :key="m.id">
                    <div class="columns is-gapless pt-4 pb-2">
                        <div class="column is-narrow"><i class="far fa-fw fa-check-circle mr-2 has-text-success"></i></div>
                        <div class="column">
                            <h5 class="has-text-weight-bold mb-2">Milestone {{ m.milestone_number }}: {{ getMilestoneRange(m) }}</h5>
                            <p class="is-size-7 has-text-grey mb-3">Approved {{ formatDate(m.reviewed_at) }}</p>

                            <b-collapse :open="false" aria-id="milestone-collapse">
                                <template #trigger="props">
                                    <a class="is-size-7">
                                        <i :class="props.open ? 'fa-chevron-up' : 'fa-chevron-down'" class="fas mr-1"></i>
                                        {{ props.open ? 'Hide details' : 'Show details' }}
                                    </a>
                                </template>
                                <div class="mt-3">
                                    <label class="label is-size-7 has-text-grey">Your reflection:</label>
                                    <p class="content-text">{{ m.reflection_text }}</p>

                                    <div v-if="m.files && m.files.length" class="mt-3">
                                        <label class="label is-size-7 has-text-grey">Attached files:</label>
                                        <div v-for="f in m.files" :key="f.id" class="mb-1">
                                            <a :href="f.preview_url || f.download_url" target="_blank" rel="noopener"><i class="far fa-paperclip mr-1"></i>{{ f.title }}</a>
                                            <span class="is-size-7 has-text-grey ml-2">{{ formatFileSize(f.size) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </b-collapse>
                        </div>
                    </div>
                </ui-card>
            </template>
        </div>

        <!-- Attachment drawer (shared with the Files page upload) -->
        <file-upload-panel
            :is-open="showAttachPanel"
            :enrollment-id="enrollmentId"
            :milestone-id="current && current.id"
            :is-stem="isStem"
            @close="showAttachPanel = false"
            @uploaded="onAttachmentUploaded"
        />
    </div>
</template>

<script>
    // Attachment deletes use the raw axios instance — cachios would key
    // the FormData body as '{}' and serve stale cached responses.
    import {client as http, axiosInstance} from "../../http_client";
    import debounce from "lodash/debounce";
    import moment from "moment";
    import UiCard from "../../components/UiCard";
    import FileUploadPanel from "../../components/course/FileUploadPanel";

    export default {
        components: {UiCard, FileUploadPanel},
        props: ['enrollmentId'],
        data: function() {
            return {
                loading: true,
                milestones: [],
                current: null,
                totalHours: 0,
                isLocked: false,
                isSelfDirected: false,
                isStem: false,
                reflectionText: '',
                submitting: false,
                showAttachPanel: false,
                // Reflection draft autosave (Notes.vue shape: idle-debounced)
                draftReady: false,
                draftDirty: false,
                draftSaving: false,
                draftSavedAt: null,
                draftSaveError: false
            };
        },
        computed: {
            completedMilestones() {
                return this.milestones
                    .filter(m => m.status === 'approved')
                    .sort((a, b) => b.milestone_number - a.milestone_number);
            },
            stemWorkLogged() {
                return !!(this.current && this.current.study_log && this.current.study_log.stem_mins > 0);
            },
            // Same duration format as the Study Log's Total Time Recorded
            totalTimeFormatted() {
                const total = Math.round(this.totalHours * 60);
                return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
            }
        },
        methods: {
            // preserveDraft keeps the typed (unsubmitted) reflection text when the
            // reload is triggered by an attachment add/remove.
            loadMilestones: function(preserveDraft = false) {
                this.loading = !preserveDraft;
                http.get(`/app/enrollments/${this.enrollmentId}/milestones`, {force: true}).then((response) => {
                    this.milestones = response.data.milestones || [];
                    this.current = response.data.current;
                    this.totalHours = response.data.total_hours || 0;
                    this.isLocked = response.data.is_locked;
                    this.isSelfDirected = response.data.is_self_directed;
                    this.isStem = response.data.stem === true;

                    // Keep the shared lock state (side nav flag, study log) in sync
                    this.$store.commit('SET_MILESTONE_LOCK', {
                        locked: !!response.data.is_locked,
                        milestone: response.data.is_locked ? response.data.current : null
                    });

                    // Pre-fill the reflection: the prior submission when changes were
                    // requested, or the autosaved draft while still pending
                    if (!preserveDraft) {
                        this.draftReady = false;
                        this.draftDirty = false;
                        this.draftSaveError = false;
                        this.draftSavedAt = null;
                        if (this.current && ['changes_requested', 'pending'].includes(this.current.status)) {
                            this.reflectionText = this.current.reflection_text || '';
                        } else {
                            this.reflectionText = '';
                        }
                        this.$nextTick(() => {
                            this.draftReady = true;
                        });
                    }

                    this.loading = false;
                }).catch((error) => {
                    console.error('Error loading milestones:', error);
                    this.loading = false;
                });
            },
            // True while the reflection form is editable (draft saves allowed)
            canDraft: function() {
                return !!(this.current && ['pending', 'changes_requested'].includes(this.current.status));
            },
            saveDraft: function() {
                if (this.draftSaving || !this.canDraft()) return Promise.resolve(false);
                this.draftSaving = true;
                // Edits made while the request is in flight re-set dirty and
                // re-arm the timer, so nothing is lost.
                this.draftDirty = false;
                return http.put(`/app/enrollments/${this.enrollmentId}/milestones/${this.current.id}/draft`, {
                    reflection_text: this.reflectionText
                }, {force: true}).then(() => {
                    this.draftSaveError = false;
                    this.draftSavedAt = new Date();
                    return true;
                }).catch((error) => {
                    console.error('Error saving reflection draft:', error);
                    this.draftDirty = true;
                    this.draftSaveError = true;
                    return false;
                }).finally(() => {
                    this.draftSaving = false;
                });
            },
            submitReflection: function() {
                if (!this.reflectionText.trim()) return;

                // A submit supersedes any queued draft save
                this.debouncedDraftSave.cancel();
                this.draftReady = false;

                this.submitting = true;
                http.post(`/app/enrollments/${this.enrollmentId}/milestones/${this.current.id}/submit`, {
                    reflection_text: this.reflectionText.trim()
                }).then(() => {
                    this.$buefy.toast.open({
                        message: 'Reflection submitted successfully!',
                        type: 'is-success'
                    });
                    this.loadMilestones();
                }).catch((error) => {
                    console.error('Error submitting reflection:', error);
                    this.$buefy.toast.open({
                        message: error.response?.data?.error || 'Error submitting reflection',
                        type: 'is-danger'
                    });
                    // Submit failed — the form is still editable, resume autosave
                    this.draftReady = true;
                }).finally(() => {
                    this.submitting = false;
                });
            },
            onAttachmentUploaded: function() {
                this.showAttachPanel = false;
                this.loadMilestones(true);
            },
            deleteAttachment: function(file) {
                axiosInstance.delete(`/app/enrollments/${this.enrollmentId}/files/${file.id}`).then(() => {
                    this.loadMilestones(true);
                }).catch((error) => {
                    console.error('Error removing attachment:', error);
                    this.$buefy.toast.open({
                        message: error.response?.data?.error || 'Error removing attachment',
                        type: 'is-danger'
                    });
                });
            },
            formatLogMins: function(mins) {
                if (!mins) return '0 min';
                if (mins < 60) return mins + ' min';
                return (Math.round((mins / 60) * 10) / 10) + ' h';
            },
            formatFileSize: function(bytes) {
                if (!bytes && bytes !== 0) return '';
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            },
            getMilestoneRange: function(milestone) {
                const prevThreshold = this.milestones
                    .filter(m => m.milestone_number < milestone.milestone_number)
                    .reduce((max, m) => Math.max(max, m.hour_threshold), 0);
                return `${prevThreshold}-${milestone.hour_threshold} Hours`;
            },
            formatDate: function(date) {
                return date ? moment(date).format('MMMM Do, YYYY') : '';
            },
            formatTime: function(date) {
                return moment(date).format('h:mm:ss a');
            }
        },
        watch: {
            reflectionText: function() {
                if (!this.draftReady || !this.canDraft()) return;
                this.draftDirty = true;
                this.debouncedDraftSave();
            }
        },
        created() {
            // Save 5s after typing pauses; a long unbroken writing session
            // still checkpoints every 30s. Non-reactive on purpose.
            this.debouncedDraftSave = debounce(() => this.saveDraft(), 5000, {maxWait: 30000});
        },
        mounted() {
            this.loadMilestones();
        },
        beforeDestroy() {
            this.debouncedDraftSave.cancel();
            if (this.draftDirty) this.saveDraft();
        }
    };
</script>

<style scoped>
    .content-text {
        white-space: pre-wrap;
        line-height: 1.6;
    }

    /* Reserve the line so the first "Draft saved" doesn't shift the layout */
    .draft-status {
        min-height: 1.4em;
        margin-top: 0.25rem;
    }

    /* Static informational box — override any inherited click affordance */
    .instructions-box,
    .instructions-box ::v-deep * {
        cursor: default;
    }

    /* AI suggestion HTML uses ol items with nested uls; give each numbered
       item breathing room so the next item isn't cramped against the list above */
    .instructions-box ::v-deep ol > li {
        margin-bottom: 1em;
    }

    .instructions-box ::v-deep ol > li > ul {
        margin-top: 0.5em;
    }

    /* Keep the duration tag visible against the grey notification box */
    .log-time-tag {
        border: 1px solid #dbdbdb;
    }

</style>
